/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
//Grid for middle fork for example.  See ui-permit/src/containers/DetailedAvailabilityConductor.jsx for more locations.

import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import { gnoment } from 'shared-ui/gnoment';
import cx from 'classnames';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Icon, Spinner, announce } from 'sarsaparilla';

import StickyTableHeaderHelper from './StickyTableHeaderHelper';
import AvailabilityRow from './AvailabilityRow';
import AvailabilityTableHeader from './AvailabilityTableHeaderNew';
import { Grid } from './Grid';

import * as types from '../../constants';
import { generateAvailabilityCellData } from '../../utils/availability-table-utils';
import { isWalkupQuotaType } from '../../utils/availability-utils-new';
import { readRuleValue, dateDiffs, getDaysBetween } from '../../utils/utilsNew';

export default class AvailabilityGrid extends React.PureComponent {
    static propTypes = {
        divisionType: PropTypes.string,
        allowFlexibleItinerary: PropTypes.bool,
        isBypassQuotaCheck: PropTypes.bool,
        allowSameDayReservation: PropTypes.bool,
        checkoutNextDay: PropTypes.bool,
        dataSource: PropTypes.arrayOf(PropTypes.shape({ permit: PropTypes.shape({}) }))
            .isRequired,
        endDate: PropTypes.any,
        hasStayLimitRule: PropTypes.bool,
        ignoreQuotaAfterFirstDay: PropTypes.bool,
        isFullView: PropTypes.bool,
        isLoading: PropTypes.bool,
        itineraryStartNextDay: PropTypes.bool,
        loading: PropTypes.bool,
        onSelectionChange: PropTypes.func.isRequired,
        overlay: PropTypes.string,
        permitId: PropTypes.string,
        quota_type: PropTypes.any,
        quotaType: PropTypes.oneOf([types.QUOTA_TYPE_ADVANCED, types.QUOTA_TYPE_WALKUP]),
        searchFilterValue: PropTypes.string,
        selectedItinerary: PropTypes.arrayOf(PropTypes.shape({})),
        shouldHideOutOfRangeSites: PropTypes.bool,
        shouldHideUnavailableSites: PropTypes.bool,
        sort: PropTypes.shape({
            column: PropTypes.string,
            isAsc: PropTypes.bool,
            isSorted: PropTypes.bool,
        }),
        startDate: PropTypes.any,
        stayLimit: PropTypes.number,
        stayLimitRule: PropTypes.shape({}),
        stayLimitMetadata: PropTypes.shape({}),
        itineraryStayLimitPerDivision: PropTypes.number,
        updateSort: PropTypes.func,
        isAvailabilityEmpty: PropTypes.bool,
        onDivisionDetailsClick: PropTypes.func,
        numOfCols: PropTypes.number,
        isSmallerGrid: PropTypes.bool,
        children: PropTypes.node,
    };

    static defaultProps = {
        allowSameDayReservation: false,
        hasStayLimitRule: false,
        ignoreQuotaAfterFirstDay: false,
        isFullView: false,
        isLoading: false,
        itineraryStartNextDay: false,
        loading: false,
        searchFilterValue: '',
        selectedItinerary: [],
        shouldHideOutOfRangeSites: true,
        shouldHideUnavailableSites: false,
        stayLimit: 0,
        stayLimitMetadata: {},
        isAvailabilityEmpty: false,
        numOfCols: types.AVAILABILITY_DAYS_TO_DISPLAY,
    };

    state = {
        dataSource: [],
        maskForSelectableDates: {},
        availabilityCellData: {},
    };

    componentDidMount() {
        this.refreshGrid();
    }

    componentDidUpdate(prevProps) {
        const {
            hasStayLimitRule,
            dataSource,
            selectedItinerary,
            startDate,
            endDate,
            searchFilterValue,
            sort,
            shouldHideOutOfRangeSites,
            shouldHideUnavailableSites,
            itineraryStartNextDay,
            numOfCols,
        } = this.props;

        // Prevent refreshGrid from firing when user selects first date and the
        // stay limit rule hasn't been returned yet
        if (selectedItinerary.length && !hasStayLimitRule) {
            return;
        }

        if (
            dataSource !== prevProps.dataSource ||
            selectedItinerary !== prevProps.selectedItinerary ||
            startDate !== prevProps.startDate ||
            endDate !== prevProps.endDate ||
            searchFilterValue !== prevProps.searchFilterValue ||
            sort !== prevProps.sort ||
            shouldHideOutOfRangeSites !== prevProps.shouldHideOutOfRangeSites ||
            shouldHideUnavailableSites !== prevProps.shouldHideUnavailableSites ||
            hasStayLimitRule !== prevProps.hasStayLimitRule ||
            itineraryStartNextDay !== prevProps.itineraryStartNextDay ||
            numOfCols !== prevProps.numOfCols
        ) {
            this.refreshGrid();
        }
    }

    onColumnSortToggle(columnName, isSorted, isAsc) {
        this.props.updateSort({ column: columnName, isSorted, isAsc });
    }

    onNameSortToggle = (isSorted, isAsc) => {
        this.onColumnSortToggle('name', isSorted, isAsc);
    };

    onAreaSortToggle = (isSorted, isAsc) => {
        this.onColumnSortToggle('district', isSorted, isAsc);
    };

    getDataWithoutUnavailableRows = (
        dataSource,
        maskForSelectableDates,
        maskForSelectedDates
    ) => {
        const { startDate, numOfCols } = this.props;
        const unavailableIds = [];
        const datesToCheck = [];

        // Create an array of dates that are shown on the grid
        for (let i = 0; i < numOfCols; i += 1) {
            datesToCheck.push(startDate.clone().add(i, 'days').format('YYYY-MM-DD'));
        }

        dataSource.forEach((divisionData) => {
            const { division } = divisionData;
            const unavailableDates = [];

            // 1) Make an array of all the dates with no availability
            datesToCheck.forEach((date) => {
                if (
                    !maskForSelectableDates[division.id][date] &&
                    !maskForSelectedDates[division.id][date]
                ) {
                    unavailableDates.push(date);
                }
            });
            // 2) if all the dates are unavailable, add the id to unavailableIds array
            if (unavailableDates.length === datesToCheck.length) {
                unavailableIds.push(division.id);
            }
        });

        if (unavailableIds.length) {
            return dataSource.filter((item) => {
                if (unavailableIds.includes(item.division.id)) {
                    return false;
                }
                return true;
            });
        }
        return dataSource;
    };

    // set all dates between startDate and endDate within the provided dateMap to the given value (default true)
    unmask = (dateMap, startDate, endDate, inclusivityFlags, desiredValue) => {
        let valueToSet = desiredValue;
        if (valueToSet === undefined) {
            valueToSet = true;
        }

        const copy = { ...dateMap };
        const keys = getDaysBetween(dateMap, startDate, endDate, inclusivityFlags);

        keys.forEach((date) => {
            copy[date] = valueToSet;
        });

        return copy;
    };

    handleClick = (permitId, divisionId, date) => {
        let previousEvent;
        if (this.props.selectedItinerary.length !== 0) {
            // WARN this is a referenced object from props, do not mutate it
            previousEvent =
                this.props.selectedItinerary[this.props.selectedItinerary.length - 1];
        }

        // if the last event is a single day event
        // and the user is clicking on it, delete the event entirely
        if (
            previousEvent &&
            previousEvent.permit.id === permitId &&
            previousEvent.division.id === divisionId &&
            previousEvent.startDate === date &&
            previousEvent.endDate === date
        ) {
            this.updateItineraryEvents(this.props.selectedItinerary.slice(0, -1));
            return;
        }

        // if the last event is a multi day event
        // and the user is clicking on the endDate of the event
        // transform the multi day event into a single day event
        if (
            previousEvent &&
            previousEvent.permit.id === permitId &&
            previousEvent.division.id === divisionId &&
            previousEvent.endDate === date
        ) {
            const newItinerary = cloneDeep(this.props.selectedItinerary);
            const newEvent = newItinerary[newItinerary.length - 1];
            newEvent.endDate = newEvent.startDate;
            this.updateItineraryEvents(newItinerary);
            return;
        }

        const { permit } = this.props.dataSource[0];
        const isWalkup = isWalkupQuotaType(this.props.quotaType);
        const isEntryDateOnly =
            readRuleValue(types.GROUP_AVAILABILITY_KEY_NAME, permit.rules) ||
            readRuleValue(types.MEMBER_AVAILABILITY_KEY_NAME, permit.rules);
        const isExtendingEntryDateOnly = previousEvent && isEntryDateOnly;
        const isExtendingEntryDateOnlyWalkup = isExtendingEntryDateOnly && isWalkup;

        // if the user is clicking on an unselectable date (walkups in the past are OK), ignore the click
        if (
            !this.state.maskForSelectableDates[divisionId][date] ||
            isExtendingEntryDateOnlyWalkup
        ) {
            // in field sales, let the user click an "unselectable date" if the permit is a isEntryDateOnly
            // write over the original event and update it.
            if (isExtendingEntryDateOnly) {
                const newItinerary = cloneDeep(this.props.selectedItinerary);
                const newEvent = newItinerary[newItinerary.length - 1];
                newEvent.division = this.props.dataSource.filter(
                    (data) => divisionId && data?.division?.id === divisionId
                )[0].division;
                newEvent.startDate = date;
                newEvent.endDate = date;
                this.updateItineraryEvents(newItinerary);
                return;
            }
            if (!isWalkup) {
                if (!this.props.isBypassQuotaCheck) return;
            }
        }

        // if the last event is in the same division as the new event
        // it means the user is extending the last event
        if (
            previousEvent &&
            previousEvent.permit.id === permitId &&
            previousEvent.division.id === divisionId
        ) {
            const newItinerary = cloneDeep(this.props.selectedItinerary);
            const newEvent = newItinerary[newItinerary.length - 1];
            newEvent.endDate = date;
            this.updateItineraryEvents(newItinerary);
            return;
        }

        // Otherwise, we are creating a brand new single day event
        const currentDivisionData = this.props.dataSource.filter(
            (data) => data.division.id === divisionId
        )[0].division;
        const newEvent = {
            permit: {
                id: currentDivisionData.permit_id,
                name: permit.name,
            },
            division: {
                name: currentDivisionData.name,
                id: currentDivisionData.id,
                type: currentDivisionData.type,
                district: currentDivisionData.district,
                is_hidden: currentDivisionData.is_hidden,
                child_distances: currentDivisionData.child_distances,
                childDivisions: currentDivisionData.childDivisions,
                children: currentDivisionData.children,
            },
            startDate: date,
            endDate: date,
        };

        // Some permits have checkout pre-selected for the next day!
        if (this.props.checkoutNextDay) {
            newEvent.endDate = gnoment(date)
                .add(1, 'd')
                .format(types.AVAILABILITY_KEY_MAP_FORMAT);
        }

        // add new event to previous events and save
        const newItineraryEvents = [...this.props.selectedItinerary, newEvent];
        this.updateItineraryEvents(newItineraryEvents, true);
    };

    updateItineraryEvents(newItineraryEvents, shouldUpdateDivisionStayLimit) {
        this.props.onSelectionChange(newItineraryEvents, shouldUpdateDivisionStayLimit);
    }

    // update mask directly
    maskBlockedDays(mask, dataObject) {
        const newMask = { ...mask };
        dataObject.forEach((data) => {
            Object.keys(data.dateMap).forEach((date) => {
                newMask[data.division.id][date] =
                    newMask[data.division.id][date] && data.dateMap[date];
            });
        });
        return newMask;
    }

    rebuildMaskForSelectedDates = (dataObject = [], itineraryEvents) => {
        const mask = {};

        if (dataObject.length === 0) return mask;

        // initially set everything as masked
        dataObject.forEach((data) => {
            mask[data.division.id] = { ...data.dateMap };
            Object.keys(mask[data.division.id]).forEach(
                (key) => (mask[data.division.id][key] = false)
            );
        });

        // unmask selected items
        itineraryEvents.forEach((itineraryEvent) => {
            // single day
            if (itineraryEvent.startDate === itineraryEvent.endDate) {
                mask[itineraryEvent.division.id][itineraryEvent.startDate] =
                    types.AVAILABILITY_SINGLE_DAY;
                return;
            }

            // multi day
            mask[itineraryEvent.division.id][itineraryEvent.startDate] =
                types.AVAILABILITY_START_MULTIDAY;
            mask[itineraryEvent.division.id][itineraryEvent.endDate] =
                types.AVAILABILITY_END_MULTIDAY;

            const divisionMask = { ...mask[itineraryEvent.division.id] };

            // we need to augment the generated division mask to allow reservations outside of availability
            const endMoment = gnoment(itineraryEvent.endDate);
            let currentDay = gnoment(itineraryEvent.startDate).add(1, 'day');
            if (currentDay.isBefore(endMoment)) {
                while (!currentDay.isSame(endMoment, 'day')) {
                    divisionMask[currentDay.format(types.AVAILABILITY_KEY_MAP_FORMAT)] =
                        types.AVAILABILITY_CONTINUE_MULTIDAY;
                    currentDay = currentDay.add(1, 'day');
                }
            }

            mask[itineraryEvent.division.id] = this.unmask(
                divisionMask,
                gnoment(itineraryEvent.startDate).utc(),
                gnoment(itineraryEvent.endDate).utc(),
                '()',
                types.AVAILABILITY_CONTINUE_MULTIDAY
            );
        });
        return mask;
    };

    // build a dateMap for every division.  If the dateMap for a date is 'true', that date is selectable
    rebuildMaskForSelectableDates = () => {
        const { dataSource, selectedItinerary, itineraryStayLimitPerDivision } =
            this.props;
        let mask = {};

        if (dataSource.length === 0) return mask;

        // initially set everything as masked
        dataSource.forEach((data) => {
            mask[data.division.id] = { ...data.dateMap };
            Object.keys(mask[data.division.id]).forEach(
                (key) => (mask[data.division.id][key] = false)
            );
        });
        // if there are no events, unmask all divisions that have isStartingPoint = true
        if (selectedItinerary.length === 0) {
            dataSource.forEach((data) => {
                if (data.division.isStartingPoint) {
                    // If the day is before the secret visibility for rolling quota, set the availability to true
                    Object.keys(mask[data.division.id]).forEach((key) => {
                        if (!data?.secretMap?.[key]) {
                            mask[data.division.id][key] = true;
                        }
                    });
                }
            });
            // and mask any blocked dates
            mask = this.maskBlockedDays(mask, dataSource);
        }
        // else ... selectedItinerary is not empty
        else {
            // compute how many days were used for each segment
            const daysUsedPerDivision = selectedItinerary.reduce(
                (runningTotalPerDivision, event) => {
                    const copy = { ...runningTotalPerDivision };
                    if (!runningTotalPerDivision[event.division.id]) {
                        copy[event.division.id] = 0;
                    }
                    copy[event.division.id] += dateDiffs(
                        gnoment(event.startDate),
                        gnoment(event.endDate),
                        false
                    );
                    return copy;
                },
                {}
            );

            // compute how many days were used overall
            let totalDaysUsed = selectedItinerary.reduce((total, event) => {
                return (
                    total +
                    dateDiffs(gnoment(event.startDate), gnoment(event.endDate), true)
                );
            }, 0);
            totalDaysUsed += 1; // count end

            const firstEvent = selectedItinerary[0];
            const lastEvent = selectedItinerary[selectedItinerary.length - 1];
            totalDaysUsed = dateDiffs(
                gnoment(firstEvent.startDate),
                gnoment(lastEvent.endDate)
            );

            // compute the maximum distance the last selection could be extended by
            const lastEventDivision = dataSource.filter(
                (data) => data.division.id === lastEvent.division.id
            )[0].division;
            let seasonalStayLimit = this.props.stayLimit;
            if (this.props.isBypassQuotaCheck) {
                seasonalStayLimit = types.OVERRIDE_STAY_LIMIT_MAX; // override season stay to use max value of 30 days
            }
            let totalRemainingDays = seasonalStayLimit - totalDaysUsed;
            let divisionRemainingDays =
                lastEventDivision.stayLimit - daysUsedPerDivision[lastEvent.division.id];

            // if itinerary stay limit, checks max days per division
            if (itineraryStayLimitPerDivision) {
                totalRemainingDays = seasonalStayLimit - totalDaysUsed;

                const lastItinerary = selectedItinerary[selectedItinerary.length - 1];
                divisionRemainingDays =
                    itineraryStayLimitPerDivision -
                    dateDiffs(
                        gnoment(lastItinerary.startDate),
                        gnoment(lastItinerary.endDate),
                        false
                    );
            }
            const remainingDays = Math.min(totalRemainingDays, divisionRemainingDays);

            // hardcoded ruby blackrock rule
            let blackRockFlag = false;
            if (
                types.RUBY_BLACKROCK_DIV_IDS.includes(lastEvent.division.id) &&
                remainingDays === 0
            ) {
                // if current stay limit is zero because you are in a blackrock
                // make sure the blackrock children are blocked too
                blackRockFlag = true;
            }

            // if we're ignoring quota after the first day, unblock the maximum distance
            if (
                lastEvent.endDate instanceof Date &&
                !isNaN(lastEvent.endDate) &&
                this.props.ignoreQuotaAfterFirstDay
            ) {
                mask[lastEvent.division.id] = this.unmask(
                    mask[lastEvent.division.id],
                    gnoment(lastEvent.endDate).utc(),
                    gnoment(lastEvent.endDate).utc().clone().add(remainingDays, 'days'),
                    '(]'
                );
            } else if (totalRemainingDays > 0) {
                // limit that maximum distance by blocking the selection upon encountering the first instance of a blocked date
                const allPossibleDays = getDaysBetween(
                    mask[lastEvent.division.id],
                    gnoment(lastEvent.endDate).utc(),
                    gnoment(lastEvent.endDate).utc().clone().add(remainingDays, 'days'),
                    '(]'
                );
                const lastEventData = dataSource.filter(
                    (data) => data.division.id === lastEvent.division.id
                )[0];
                let foundBlocker = false;

                let remainingDaysForCurrent = remainingDays;
                allPossibleDays.forEach((date, index) => {
                    if (foundBlocker) {
                        return;
                    }
                    if (!lastEventData.dateMap[date]) {
                        remainingDaysForCurrent = index;
                        foundBlocker = true;
                    }
                });
                // unmask the days that can be extended
                mask[lastEvent.division.id] = this.unmask(
                    mask[lastEvent.division.id],
                    gnoment(lastEvent.endDate).utc(),
                    gnoment(lastEvent.endDate)
                        .utc()
                        .clone()
                        .add(remainingDaysForCurrent, 'days'),
                    '(]'
                );

                // enable children (if selected)
                lastEvent.division.children.forEach((childId) => {
                    const canChildBeUnmaskedWithoutSameDayReservation =
                        totalRemainingDays > 0 &&
                        lastEvent.endDate !== lastEvent.startDate;
                    const canChildBeUnmaskedWithSameDayReservation = totalDaysUsed >= 0;

                    if (this.props.itineraryStartNextDay) {
                        // for Ruby, if the last div was blackrock AND remaining days are 0 (blackRockFlag)
                        // AND the childId is blackrock, do NOT unmask.
                        if (
                            !blackRockFlag ||
                            !types.RUBY_BLACKROCK_DIV_IDS.includes(childId) ||
                            (!blackRockFlag &&
                                !types.RUBY_BLACKROCK_DIV_IDS.includes(childId))
                        ) {
                            mask[childId] = this.unmask(
                                mask[childId],
                                gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                '[]'
                            );
                        }
                    } else if (this.props.allowFlexibleItinerary) {
                        // unmask childId for today and tomorrow!
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc(),
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                            '[]'
                        );
                    } else if (
                        this.props.allowSameDayReservation
                            ? canChildBeUnmaskedWithSameDayReservation
                            : canChildBeUnmaskedWithoutSameDayReservation
                    ) {
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc(),
                            gnoment(lastEvent.endDate).utc(),
                            '[]'
                        );
                    }
                });

                // enable child_distances children for Olympic
                if (
                    lastEvent.division.child_distances &&
                    this.props.shouldHideOutOfRangeSites
                ) {
                    const childIds = Object.keys(lastEvent.division.child_distances);

                    childIds.forEach((childId) => {
                        const canChildBeUnmaskedWithoutSameDayReservation =
                            totalRemainingDays > 0 &&
                            lastEvent.endDate !== lastEvent.startDate;
                        const canChildBeUnmaskedWithSameDayReservation =
                            totalDaysUsed >= 0;

                        if (!mask[childId]) {
                            mask[childId] = {};
                        }

                        if (this.props.itineraryStartNextDay) {
                            // If cannot go longer on the current division (due to itineraryStayLimitPerDivision), block the next day for the current division
                            if (
                                remainingDaysForCurrent !== 0 ||
                                childId !== lastEvent.division.id
                            ) {
                                mask[childId] = this.unmask(
                                    mask[childId],
                                    gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                    gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                    '[]'
                                );
                            }
                        } else if (this.props.allowFlexibleItinerary) {
                            // unmask childId for today and tomorrow!
                            mask[childId] = this.unmask(
                                mask[childId],
                                gnoment(lastEvent.endDate).utc(),
                                gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                '[]'
                            );
                        } else if (
                            this.props.allowSameDayReservation
                                ? canChildBeUnmaskedWithSameDayReservation
                                : canChildBeUnmaskedWithoutSameDayReservation
                        ) {
                            mask[childId] = this.unmask(
                                mask[childId],
                                gnoment(lastEvent.endDate).utc(),
                                gnoment(lastEvent.endDate).utc(),
                                '[]'
                            );
                        }
                    });
                }
                // enable all the sites for Olympic admin people if shouldHideOutOfRangeSites is false
                // or if selecting an admin site (because it has no child_distances array)
                if (
                    (lastEvent.division.child_distances &&
                        !this.props.shouldHideOutOfRangeSites) ||
                    (lastEvent.permit.id === '4098362' && lastEvent.division.is_hidden)
                ) {
                    const ids = dataSource.map((item) => item.division.id);

                    ids.forEach((id) => {
                        const canChildBeUnmaskedWithoutSameDayReservation =
                            totalRemainingDays > 0 &&
                            lastEvent.endDate !== lastEvent.startDate;
                        const canChildBeUnmaskedWithSameDayReservation =
                            totalDaysUsed >= 0;

                        if (!mask[id]) {
                            mask[id] = {};
                        }

                        if (this.props.itineraryStartNextDay) {
                            mask[id] = this.unmask(
                                mask[id],
                                gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                '[]'
                            );
                        } else if (this.props.allowFlexibleItinerary) {
                            // unmask id for today and tomorrow!
                            mask[id] = this.unmask(
                                mask[id],
                                gnoment(lastEvent.endDate).utc(),
                                gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                                '[]'
                            );
                        } else if (
                            this.props.allowSameDayReservation
                                ? canChildBeUnmaskedWithSameDayReservation
                                : canChildBeUnmaskedWithoutSameDayReservation
                        ) {
                            mask[id] = this.unmask(
                                mask[id],
                                gnoment(lastEvent.endDate).utc(),
                                gnoment(lastEvent.endDate).utc(),
                                '[]'
                            );
                        }
                    });
                }

                // and finally mask any blocked dates
                this.maskBlockedDays(mask, dataSource);
            } else if (totalRemainingDays === 0) {
                // last day, can still unmask children to complete a trip (pere marquette uses this)
                lastEvent.division.children.forEach((childId) => {
                    const canChildBeUnmaskedWithoutSameDayReservation =
                        totalRemainingDays > 0 &&
                        lastEvent.endDate !== lastEvent.startDate;
                    const canChildBeUnmaskedWithSameDayReservation = totalDaysUsed >= 0;
                    const stayLimitMetadata = get(
                        this.props,
                        `stayLimitMetadata.${childId}`,
                        ''
                    );

                    if (
                        this.props.allowFlexibleItinerary ||
                        this.props.allowSameDayReservation
                            ? canChildBeUnmaskedWithSameDayReservation
                            : canChildBeUnmaskedWithoutSameDayReservation
                    ) {
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc(),
                            gnoment(lastEvent.endDate).utc(),
                            '[]'
                        );
                    } else if (blackRockFlag && stayLimitMetadata) {
                        // if metadata exists, apply "1" to it right now
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd').utc(),
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd').utc(),
                            '[]'
                        );
                    }
                });
                // and finally mask any blocked dates
                this.maskBlockedDays(mask, dataSource);
            }
        }
        return mask;
    };

    refreshGrid = () => {
        // TODO quota_type appears to never be defined ... why is it a thing and can it be removed?

        const {
            selectedItinerary,
            sort,
            quota_type,
            isBypassQuotaCheck,
            startDate,
            dataSource,
            searchFilterValue,
            numOfCols,
        } = this.props;

        const maskForSelectableDates = this.rebuildMaskForSelectableDates();
        const maskForSelectedDates = this.rebuildMaskForSelectedDates(
            dataSource,
            selectedItinerary
        );
        const availabilityCellData = {};

        // remove any rows that have no availability
        let dataSourceFiltered = this.props.shouldHideUnavailableSites
            ? this.getDataWithoutUnavailableRows(
                  dataSource,
                  maskForSelectableDates,
                  maskForSelectedDates
              )
            : dataSource;

        // show only filter results if there's a searchFilterValue
        if (searchFilterValue && searchFilterValue.length) {
            dataSourceFiltered = dataSourceFiltered.filter((item) => {
                const comboName = `${item.division.name.toUpperCase()} ${item.division.district.toUpperCase()}`;
                const searchTerm = searchFilterValue.toUpperCase();

                return comboName.search(searchTerm) >= 0;
            });
        }

        const newDataSource = orderBy(
            dataSourceFiltered,
            `division.${sort.column}`,
            sort.isAsc ? 'asc' : 'desc'
        );

        newDataSource.forEach((divisionData) => {
            const { division } = divisionData;

            availabilityCellData[division.id] = range(numOfCols).map((i) =>
                generateAvailabilityCellData(
                    divisionData,
                    i,
                    maskForSelectableDates,
                    maskForSelectedDates,
                    quota_type,
                    isBypassQuotaCheck,
                    startDate,
                    dataSource[0].permit
                )
            );
        });

        this.setState(({ dataSource: prevDataSource }) => {
            this.announceGridSizeChange(prevDataSource, newDataSource);

            return {
                dataSource: newDataSource,
                maskForSelectableDates,
                availabilityCellData,
            };
        });
    };

    announceGridSizeChange = (dataSource, newDataSource) => {
        const { divisionType } = this.props;
        if (newDataSource.length === 0) {
            announce(
                'No sites are available to choose from on the Detailed Availability Grid'
            );
        } else if (dataSource.length === 0 && newDataSource.length > 0) {
            announce(
                'Available sites to choose from are now available on the Detailed Availability Grid'
            );
        } else if (dataSource.length !== newDataSource.length) {
            const announceText =
                newDataSource.length > dataSource.length
                    ? `Detailed Availability Grid has grown. More ${divisionType} are available to choose from`
                    : `Detailed Availability Grid has shrunk. Less ${divisionType} are available to choose from`;
            announce(announceText);
        }
    };

    render() {
        const { isSmallerGrid, numOfCols, children } = this.props;
        const shouldAnimateRowChanges = this.state.dataSource.length <= 25;
        const hasAreaData =
            !!this.state.dataSource.length &&
            !!this.state.dataSource[0].division.district;
        if (this.props.loading) {
            return <div>Loading</div>;
        }

        return (
            <div className="per-availability-table-container">
                {!this.props.overlay && (
                    <StickyTableHeaderHelper
                        scrollingContainerId="per-availability-main"
                        isFullView={this.props.isFullView}
                    >
                        {children}
                        <div
                            aria-hidden="true"
                            className="sticky-availability-table-header"
                        >
                            <Grid
                                className={cx('detailed-availability-grid-new', {
                                    'has-area-data': hasAreaData && !isSmallerGrid,
                                    'mobile-app': isSmallerGrid,
                                })}
                            >
                                <AvailabilityTableHeader
                                    divisionType={this.props.divisionType}
                                    hasAreaData={hasAreaData && !isSmallerGrid}
                                    permitId={this.props.permitId}
                                    sort={this.props.sort}
                                    startDate={this.props.startDate}
                                    onNameSortToggle={this.onNameSortToggle}
                                    onAreaSortToggle={this.onAreaSortToggle}
                                    numOfCols={numOfCols}
                                />
                            </Grid>
                        </div>
                    </StickyTableHeaderHelper>
                )}

                <Grid
                    className={cx('detailed-availability-grid-new', {
                        'has-area-data': hasAreaData && !isSmallerGrid,
                        'mobile-app': isSmallerGrid,
                    })}
                    caption={`Availability by ${this.props.divisionType} and Dates`}
                >
                    <AvailabilityTableHeader
                        divisionType={this.props.divisionType}
                        hasAreaData={hasAreaData && !isSmallerGrid}
                        sort={this.props.sort}
                        startDate={this.props.startDate}
                        onNameSortToggle={this.onNameSortToggle}
                        onAreaSortToggle={this.onAreaSortToggle}
                        numOfCols={numOfCols}
                    />
                    <div>
                        {shouldAnimateRowChanges && (
                            <TransitionGroup component={null}>
                                {this.state.dataSource.map((divisionData) => {
                                    return (
                                        <CSSTransition
                                            key={divisionData.division.id}
                                            timeout={450}
                                            classNames="collapse"
                                        >
                                            <AvailabilityRow
                                                hasAreaData={hasAreaData}
                                                divisionData={divisionData}
                                                availabilityCellData={
                                                    this.state.availabilityCellData
                                                }
                                                onDivisionDetailsClick={
                                                    this.props.onDivisionDetailsClick
                                                }
                                                handleClick={this.handleClick}
                                                isSmallerGrid={isSmallerGrid}
                                                numOfCols={numOfCols}
                                            />
                                        </CSSTransition>
                                    );
                                })}
                            </TransitionGroup>
                        )}
                        {!shouldAnimateRowChanges && (
                            <>
                                {this.state.dataSource.map((divisionData) => {
                                    return (
                                        <AvailabilityRow
                                            key={divisionData.division.id}
                                            hasAreaData={hasAreaData}
                                            divisionData={divisionData}
                                            availabilityCellData={
                                                this.state.availabilityCellData
                                            }
                                            onDivisionDetailsClick={
                                                this.props.onDivisionDetailsClick
                                            }
                                            handleClick={this.handleClick}
                                            isSmallerGrid={isSmallerGrid}
                                            numOfCols={numOfCols}
                                        />
                                    );
                                })}
                            </>
                        )}

                        {this.state.dataSource.length === 0 && !this.props.isLoading && (
                            <div className="no-rows-indicator-wrap">
                                <div className="no-rows-indicator-content">
                                    <Icon iconName="info-outline" size="md" />
                                    {this.props.isAvailabilityEmpty ? (
                                        <span className="text">
                                            There is no availability data. The location
                                            may be closed or out of season.
                                        </span>
                                    ) : (
                                        <span className="text">
                                            No sites are available or match your criteria
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {this.props.isLoading && (
                            <div>
                                <div className="loading-spacer" />
                                <div className="loading-overlay">
                                    <Spinner size="xl" />
                                    Fetching availabilities
                                </div>
                            </div>
                        )}
                    </div>
                </Grid>
            </div>
        );
    }
}
